// src/utils/assetCellHelpers.js
// -----------------------------------------------------------------------------
// Shared, framework-agnostic helpers for asset/virtual table cells.
// - No React imports here
// - Tree-shakable pure helpers + lightweight caches
// -----------------------------------------------------------------------------

// Unwraps the common Firestore "value" nesting 1–2 levels deep
export const unwrapValue = (x) => {
  let curr = x;
  for (let i = 0; i < 2; i++) {
    if (curr && typeof curr === "object" && !Array.isArray(curr) && "value" in curr) {
      curr = curr.value;
    } else {
      break;
    }
  }
  return curr;
};

// Global (per-tab) preview cache: assetId -> { previewUrl, meta }
export const previewCache =
  typeof window !== "undefined"
    ? (window.__previewCache ||= new Map())
    : new Map();
// Map<string, { previewUrl: string|null, meta: any|null }>

// Parse once per row: WeakMap so rows can GC
const linkedMapByRow = new WeakMap();

/**
 * getLinkedForRow(row) -> Map<attachUfi, linkedEntry> | null
 * Expects row._linkedAssetsJson (stringified array authored by your backend).
 */
export const getLinkedForRow = (row) => {
  if (!row || typeof row !== "object") return null;
  if (linkedMapByRow.has(row)) return linkedMapByRow.get(row);

  const raw = row?._linkedAssetsJson;
  if (!raw || typeof raw !== "string") {
    linkedMapByRow.set(row, null);
    return null;
  }
  try {
    const arr = JSON.parse(raw);
    const map = new Map();
    if (Array.isArray(arr)) {
      for (const entry of arr) {
        if (entry && typeof entry === "object" && entry.ufi) {
          map.set(entry.ufi, entry);
        }
      }
    }
    linkedMapByRow.set(row, map);
    return map;
  } catch {
    linkedMapByRow.set(row, null);
    return null;
  }
};

/**
 * extractFirstAssetId(row, ufi) -> string|null
 * Handles allowMultiple arrays and id/assetId fields.
 */
export const extractFirstAssetId = (row, ufi) => {
  const cell = row?.responses?.[ufi];
  const raw = (cell && typeof cell === "object" && "value" in cell) ? cell.value : cell;
  const x = unwrapValue(raw);

  if (Array.isArray(x)) {
    const first = x.find((it) => it && typeof it === "object" && (it.assetId || it.id));
    return first ? (first.assetId || first.id) : null;
  }
  if (x && typeof x === "object") {
    return x.assetId || x.id || null;
  }
  return null;
};

/**
 * extractAssetish(raw) -> string
 * Returns a human-friendly text for asset-like values (arrays/objects/primitives).
 */
export const extractAssetish = (raw) => {
  const x = unwrapValue(raw);

  if (Array.isArray(x)) {
    return x
      .map((it) => extractAssetish(it))
      .filter((s) => s !== "")
      .join(", ");
  }

  if (x && typeof x === "object") {
    const inner = unwrapValue(x);
    const id =
      inner?.assetId ||
      inner?.id ||
      (typeof inner?.value === "string" ? inner.value : "");
    return id ? String(id) : "";
  }

  return x == null ? "" : String(x);
};

/**
 * parseVirtualSpec(col) -> { role, ufi, aufUfi, provider, outputKey, metricKey }
 * Supports:
 *   - explicit metadata:
 *       { kind:'virtual', role:'thumb'|'version'|'assetType'|'assetId'|'aufField', ufi, aufUfi? }
 *       { kind:'virtual', role:'providerOutput', provider, outputKey }
 *       { kind:'virtual', role:'providerMetric', provider, metricKey }
 *   - id-based:
 *       "thumb@<ufi>"
 *       "assetId@<ufi>"
 *       "assetType@<ufi>"
 *       "version@<ufi>"
 *       "aufField@<attachUfi>@<aufUfi>"
 *       "providerOutput@<provider>@<outputKey>"
 *       "providerMetric@<provider>@<metricKey>"
 */
export const parseVirtualSpec = (col) => {
  let role = col?.role || "";
  let ufi = col?.ufi || "";
  let aufUfi = col?.aufUfi || "";
  let provider = col?.provider || "";
  let outputKey = col?.outputKey || "";
  let metricKey = col?.metricKey || "";

  if (!role && typeof col?.id === "string") {
    const id = col.id;
    if (id.startsWith("thumb@"))      { role = "thumb";      ufi = id.split("@")[1] || ""; }
    else if (id.startsWith("assetId@"))  { role = "assetId";    ufi = id.split("@")[1] || ""; }
    else if (id.startsWith("assetType@")){ role = "assetType";  ufi = id.split("@")[1] || ""; }
    else if (id.startsWith("version@"))  { role = "version";    ufi = id.split("@")[1] || ""; }
    else if (id.startsWith("aufField@")) {
      role = "aufField";
      const parts = id.split("@"); // ["aufField", "<attachUfi>", "<aufUfi>"]
      ufi = parts[1] || "";
      aufUfi = parts[2] || "";
    } else if (id.startsWith("providerOutput@")) {
      role = "providerOutput";
      const parts = id.split("@"); // ["providerOutput", "<provider>", "<outputKey>"]
      provider = parts[1] || "";
      outputKey = parts.slice(2).join("@") || "";
    } else if (id.startsWith("providerMetric@")) {
      role = "providerMetric";
      const parts = id.split("@"); // ["providerMetric", "<provider>", "<metricKey>"]
      provider = parts[1] || "";
      metricKey = parts.slice(2).join("@") || "";
    }
  }

  return {
    role,
    ufi,
    aufUfi,
    provider,
    outputKey,
    metricKey,
  };
};
